import { createFileRoute } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { SetupCopyBlock } from "@/components/setup/SetupCopyBlock";
import { SetupExamplePrompts } from "@/components/setup/SetupExamplePrompts";
import { AGENTREACHER_MCP_URL } from "@/components/setup/setup-config";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/connect-chatgpt";

export const Route = createFileRoute(path)({
	component: ConnectChatgpt,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "Connect ChatGPT to AgentReacher",
			description:
				"Four-step walkthrough to add the AgentReacher MCP server to ChatGPT. Open Settings, turn on developer mode, paste the URL, and start drafting posts.",
		}),
});

const STEPS = [
	{
		title: "Settings → Apps",
		body: "In ChatGPT, open Settings and go to Apps. This is where every app you have added shows up.",
		image: "/chatgpt-setup/app_overview.webp",
		alt: "ChatGPT Settings showing the Apps overview",
	},
	{
		title: "Turn on developer mode",
		body: "Scroll to Advanced and switch on Developer mode. This unlocks the option to add your own MCP connector.",
		image: "/chatgpt-setup/developer_mode.webp",
		alt: "ChatGPT Advanced settings with Developer mode toggle on",
	},
	{
		title: "Create a new app",
		body: "Go back to Apps and click the option to add a new custom MCP app. Paste the AgentReacher URL below, set Authentication to OAuth, and upload the AgentReacher logo as the icon.",
		image: "/chatgpt-setup/create_new_app.webp",
		alt: "ChatGPT custom MCP app creation form",
	},
	{
		title: "App is connected",
		body: "Once ChatGPT confirms, AgentReacher shows up in your apps. Start a new chat and ask ChatGPT to draft, schedule, or publish posts.",
		image: "/chatgpt-setup/created_success.webp",
		alt: "ChatGPT confirming AgentReacher has been added",
	},
];

function ConnectChatgpt() {
	return (
		<MarketingPageShell>
			<MarketingHero
				agent="chatgpt"
				title="Connect ChatGPT to AgentReacher"
				description="Add the AgentReacher connector in ChatGPT, sign in once, and ChatGPT can start working inside your social media workspace."
				primaryCta={{ label: "Start Now", to: "/choose-plan" }}
				secondaryCta={{ label: "Sign in", to: "/login" }}
			/>

			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-14">
				<div className="grid gap-5">
					{STEPS.map((step, index) => (
						<div
							className="grid gap-4 rounded-md border bg-background p-4 sm:grid-cols-[0.95fr_1.05fr] sm:p-5"
							key={step.title}
						>
							<div className="flex gap-3">
								<span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
									{index + 1}
								</span>
								<div className="min-w-0 flex-1">
									<h3 className="flex h-8 items-center text-base font-semibold text-foreground">
										{step.title}
									</h3>
									<p className="mt-1 text-sm leading-6 text-muted-foreground">
										{step.body}
									</p>
									{index === 2 ? (
										<div className="mt-3 flex flex-col gap-2">
											<SetupCopyBlock text={AGENTREACHER_MCP_URL} />
											<a
												href="/apple-touch-icon.png"
												download="agentreacher-logo.png"
												className="inline-flex w-fit items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-foreground/20 hover:bg-muted/40"
											>
												<Download className="size-3.5" />
												Download AgentReacher logo
											</a>
										</div>
									) : null}
								</div>
							</div>
							<div className="overflow-hidden rounded-md border bg-muted/30">
								<img
									src={step.image}
									alt={step.alt}
									loading="lazy"
									decoding="async"
									className="block h-auto w-full object-cover"
								/>
							</div>
						</div>
					))}
				</div>
			</section>

			<SetupExamplePrompts agentName="ChatGPT" />
		</MarketingPageShell>
	);
}
